// ConfirmDialog.tsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaExclamationCircle, FaCheckCircle, FaInfoCircle } from "react-icons/fa";

type ConfirmDialogProps = {
  show: boolean;
  title?: string;
  icon?: React.ReactNode;
  message: any;
  onConfirm: (e: any, value?: string) => void; // ✅ send text value if available
  onCancel: (e: any) => void;
  loading?: boolean;
  confirmText?: string;
  cancelText?: string;
  mode?: "modal" | "inline";
  positionStyle?: React.CSSProperties;
  variant?: "danger" | "info" | "success";

  // ✅ New optional text input props
  showInput?: boolean;
  inputLabel?: string;
  inputPlaceholder?: string;
  defaultInputValue?: string;
};

export default function ConfirmDialog({
  show,
  title = "Confirm Action",
  icon,
  message,
  onConfirm,
  onCancel,
  loading = false,
  confirmText = "Yes",
  cancelText = "Cancel",
  mode = "inline",
  positionStyle = { top: "100%", left: 0, width: 260 },
  variant = "info",

  // ✅ New input props
  showInput = false,
  inputLabel,
  inputPlaceholder = "Enter text...",
  defaultInputValue = "",
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = useState(defaultInputValue);

  // Reset text when reopened
  useEffect(() => {
    if (show) setInputValue(defaultInputValue);
  }, [show, defaultInputValue]);

  const variantIconMap = {
    danger: <FaExclamationCircle className="text-danger me-2" size={18} />,
    info: <FaInfoCircle className="text-info me-2" size={18} />,
    success: <FaCheckCircle className="text-success me-2" size={18} />,
  };

  const confirmBtnClass = {
    danger: "btn-danger",
    info: "btn-primary",
    success: "btn-success",
  }[variant];

  icon = icon ?? variantIconMap[variant];

  const TitleBlock = () => (
    <div className="d-flex align-items-center border-bottom pb-2 mb-3" style={{ gap: "8px" }}>
      {icon}
      <h6 className="mb-0 fw-semibold" style={{ fontSize: "14px" }}>{title}</h6>
    </div>
  );

  const InputBlock = () =>
    showInput ? (
      <div className="mb-3">
        {inputLabel && <label className="form-label small fw-semibold">{inputLabel}</label>}
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={inputPlaceholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={loading}
        />
      </div>
    ) : null;

  const handleConfirm = (e: any) => {
    onConfirm(e, showInput ? inputValue : undefined);
  };

  const Content = (
    <div className="modal-content p-3 bg-theme-card border rounded shadow-sm">
      <TitleBlock />
      <p className="small text-muted-theme mb-3">{message}</p>
      <InputBlock />
      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={loading}>
          {cancelText}
        </button>
        <button className={`btn btn-sm ${confirmBtnClass}`} onClick={handleConfirm} disabled={loading}>
          {loading ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            confirmText
          )}
        </button>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {show && (
        mode === "modal" ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          >
            <motion.div
              className="modal-dialog modal-dialog-centered"
              initial={{ scale: 0.95, y: -20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {Content}
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="position-absolute bg-theme-card border rounded shadow-sm p-3 z-3"
            style={positionStyle}
          >
            {Content}
          </motion.div>
        )
      )}
    </AnimatePresence>
  );
}
